import { usePatientInfoContext } from 'features/patient/_utils/patient-info/hook-context-patientinfo';
import { useMemo } from 'react';

import { Alert, Flex } from 'antd';
import { Title } from 'components/mvp-typography';
import { CgsInventory } from 'features/patient/assessment/questionnaires/cgs/cgsSlice';
import {
  useGetCGSInfo,
  useGetConcussions
} from 'lib/hooks/tanstack/use-getInventoryInfo';
import { CRxPlotlyComponentReference } from '../crx-plotly-import';
import {
  getPlotlyConfigForConcussionComplexityProgression,
  getPlotlyConfigForTotalSymptomTracker
} from './utils/plotly-configs';

interface DisplayGraphProps {
  /**
   * WARNING: This is a hardcoded width. We use this to ensure the width of the plotly graph matches the parent container.
   *
   * Failed solutions:
   * - Setting `autosize`
   * - Setting `responsive`
   * - Setting parent container widths to `100%`
   */
  width: number;
}

/**
 * 2025-08-01 Transfered to Typescript from https://github.com/concussionrx/ai-and-analytics/blob/8f81b6006c8e73bb5dac4730032bc0b6898f1dec/src/data_loader.py#L92
 * Compute the patients total symptom score
 *
 * This only uses CGS
 *
 * @param cgsData
 * @returns The total CGS score for an assessment
 */
export function computeTotalScore(cgsData: CgsInventory['data']) {
  const excludedScores = [
    'Physical',
    'Cognitive',
    'Emotional',
    'Sleep',
    'Client number',
    'Total Score'
  ];

  return Object.keys(cgsData)
    .filter(key => !excludedScores.includes(key))
    .reduce((acc, key) => acc + cgsData[key], 0);
}

export const NScoreWindow = 3;
export function DisplayTotalSymptomTracker(props: DisplayGraphProps) {
  const { patientInfo } = usePatientInfoContext();
  const cgsData = useGetCGSInfo(patientInfo.patient.id!);

  const plotlyConfig = useMemo(
    () =>
      getPlotlyConfigForTotalSymptomTracker(cgsData.data ?? [], props.width),
    [cgsData.data, props.width]
  );

  return (
    <div
      style={{
        width: props.width
      }}
    >
      <Flex gap={'small'} vertical>
        <Title level={3}>Total Symptom Score Tracker</Title>
        {cgsData.data?.length === 1 && (
          <Alert
            type="warning"
            message={`There are less than 2 records. With more records a trend line can be generated.`}
          />
        )}
      </Flex>
      <CRxPlotlyComponentReference
        data={plotlyConfig.data}
        layout={plotlyConfig.layout}
        config={plotlyConfig.config}
      />
    </div>
  );
}

export function DisplayConcussionComplexityProgression(
  props: DisplayGraphProps
) {
  const { patientInfo } = usePatientInfoContext();
  const concussionsQuery = useGetConcussions(patientInfo.patient.id!);
  const plotlyConfig = useMemo(
    () =>
      getPlotlyConfigForConcussionComplexityProgression(
        concussionsQuery.data ?? [],
        props.width
      ),
    [concussionsQuery.data, props.width]
  );
  return (
    <div
      style={{
        width: props.width
      }}
    >
      <Flex gap={'small'} vertical>
        <Title level={3}>Concussion Complexity Over Time</Title>
        {concussionsQuery.data?.length === 1 && (
          <Alert
            type="warning"
            message={
              'There are less than 2 records. With more records a trend line can be generated.'
            }
          />
        )}
      </Flex>
      <CRxPlotlyComponentReference
        data={plotlyConfig.data}
        layout={plotlyConfig.layout}
        config={plotlyConfig.config}
      />
    </div>
  );
}
